/* 사출기 동작 모델 엔진
 * SEORO-machine-demo.html 의 MachineEngine(타임라인·보간)과 재생 로직(프레임 준비·모프)을 React 에서 쓸 수 있게 옮겼다.
 *
 * 원리: Figma 프로토타입 프레임(SVG)들을 순서대로 이어 하나의 타임라인을 만들고, 인접 프레임 사이는
 * 같은 이름(id 경로)의 요소끼리 숫자 속성을 선형 보간해 부드럽게 넘어간다(SMART_ANIMATE 재현).
 * 컨테이너에는 항상 SVG 두 장(현재·다음 프레임)이 겹쳐 놓이고 opacity 로 교차한다. */

const numPattern = /[-+]?(?:\d*\.)?\d+(?:[eE][-+]?\d+)?/g;

/* 형폐 → 사출 → 보압 → 냉각 → 형개 가 시작되는 프레임 인덱스 (데모 phases[].start 와 동일) */
export const PHASE_START_INDEX = [0, 3, 7, 10, 13];

/* 가동 판정 창(초). 백엔드 EquipmentLiveStatus 와 같은 값. */
export const RUNNING_WINDOW_SEC = 10 * 60;

/* 두 속성값 사이의 보간 함수. 같은 형태의 숫자열이거나 #rrggbb 색이면 함수, 아니면 null(보간 불가). */
export function interpolate(a, b) {
  if (a === b) return null;
  if (/^#[0-9a-f]{6}$/i.test(a) && /^#[0-9a-f]{6}$/i.test(b)) {
    const aa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
    const bb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
    return (t) => '#' + aa.map((v, i) => Math.round(v + (bb[i] - v) * t).toString(16).padStart(2, '0')).join('');
  }
  if (a.includes('url(') || b.includes('url(')) return null;
  const av = a.match(numPattern);
  const bv = b.match(numPattern);
  if (!av || !bv || av.length !== bv.length || a.replace(numPattern, '@') !== b.replace(numPattern, '@')) return null;
  const starts = av.map(Number);
  const ends = bv.map(Number);
  return (t) => {
    let i = 0;
    return a.replace(numPattern, () => {
      const k = i++;
      return String(Math.round((starts[k] + (ends[k] - starts[k]) * t) * 10000) / 10000);
    });
  };
}

/* states: [{ id, next, wait, duration, type }] → { segments, starts, total } */
export function buildTimeline(states) {
  let clock = 0;
  const segments = [];
  const starts = [];
  states.forEach((s, i) => {
    starts.push(clock);
    if (!s.next) return;
    const target = states.findIndex((x) => x.id === s.next);
    if (target < 0) throw new Error(`machine model: missing frame ${s.next}`);
    segments.push({ index: i, target, start: clock, wait: s.wait, duration: s.duration, end: clock + s.wait + s.duration, type: s.type });
    clock += s.wait + s.duration;
  });
  segments.push({ index: states.length - 1, target: states.length - 1, start: clock, wait: 1, duration: 0, end: clock + 1, type: 'HOLD' });
  return { segments, starts, total: clock + 1 };
}

export function locate(timeline, time) {
  const t = Math.max(0, Math.min(timeline.total, time));
  const seg = timeline.segments.find((s) => t < s.end) || timeline.segments[timeline.segments.length - 1];
  return { ...seg, progress: seg.duration ? Math.max(0, Math.min(1, (t - seg.start - seg.wait) / seg.duration)) : 0 };
}

/* 단계 phaseIdx 가 차지하는 타임라인 구간 [start, end) */
export function phaseTimeRange(timeline, phaseIdx) {
  const start = timeline.starts[PHASE_START_INDEX[phaseIdx]];
  const end = phaseIdx + 1 < PHASE_START_INDEX.length
    ? timeline.starts[PHASE_START_INDEX[phaseIdx + 1]]
    : timeline.total;
  return [start, end];
}

/* 실제 설비의 사이클 안 위치 추정.
 * 백엔드에 공정 단계 신호가 없어, 마지막 샷 수신 시각과 설정 시간(형폐·사출·보압·형개, 나머지는 냉각)으로
 * 지금이 어느 단계의 어디쯤인지 계산한다. 가동 창을 벗어나면 null.
 * durations: { close, inject, hold, open } (초) */
export function estimateCyclePosition(nowMs, lastShotMs, cycleSec, durations) {
  const cycle = Number(cycleSec);
  if (!lastShotMs || !Number.isFinite(cycle) || cycle <= 0) return null;
  const elapsed = (nowMs - lastShotMs) / 1000;
  if (elapsed < 0 || elapsed > RUNNING_WINDOW_SEC) return null;
  const t = elapsed % cycle;
  const close = Number(durations.close) || 0;
  const inject = Number(durations.inject) || 0;
  const hold = Number(durations.hold) || 0;
  const open = Number(durations.open) || 0;
  const cool = Math.max(0, cycle - close - inject - hold - open);
  const lens = [close, inject, hold, cool, open];
  let acc = 0;
  for (let i = 0; i < lens.length; i++) {
    if (t < acc + lens[i] || i === lens.length - 1) {
      const progress = lens[i] > 0 ? Math.max(0, Math.min(0.999, (t - acc) / lens[i])) : 0;
      return { idx: i, progress };
    }
    acc += lens[i];
  }
  return null;
}

/* ---- SVG 프레임 준비 ---- */

let templateCache = null; // 프레임 파싱은 비싸므로 모듈 단위로 한 번만

function parseTemplates(states) {
  if (templateCache && templateCache.source === states) return templateCache.templates;
  const parser = new DOMParser();
  const templates = states.map((state, i) => {
    const doc = parser.parseFromString(state.svg, 'image/svg+xml');
    if (doc.querySelector('parsererror')) throw new Error(`machine model: invalid frame ${state.id}`);
    const svg = doc.documentElement;
    svg.removeAttribute('width');
    svg.removeAttribute('height');
    svg.setAttribute('aria-hidden', 'true');
    /* id 는 프레임마다 겹치므로 접두어를 붙여 문서 안에서 유일하게 만들고,
     * 모프 매칭용 원래 이름은 data-key 에 남긴다(Figma 가 붙인 _2508_xxxx 꼬리표는 제거). */
    const ids = new Map();
    svg.querySelectorAll('[id]').forEach((n) => {
      const old = n.id;
      ids.set(old, `s${i}-${old}`);
      n.dataset.key = old.replace(/_2508_\d+/g, '');
      n.id = ids.get(old);
    });
    svg.querySelectorAll('*').forEach((n) => {
      for (const a of Array.from(n.attributes)) {
        if (a.value.includes('url(#')) {
          n.setAttribute(a.name, a.value.replace(/url\(#([^)]*)\)/g, (m, id) => `url(#${ids.get(id) || id})`));
        }
      }
    });
    return svg;
  });
  templateCache = { source: states, templates };
  return templates;
}

function keyMap(svg) {
  const map = new Map();
  svg.querySelectorAll('*').forEach((n) => {
    let key = n.dataset.key;
    if (key && key.startsWith('Property 1=')) return;
    if (!key) {
      const parent = n.parentElement;
      if (!parent) return;
      const p = parent.dataset.key;
      if (!p) return;
      const siblings = Array.from(parent.children).filter((c) => c.tagName === n.tagName);
      key = `${p}/${n.tagName}/${siblings.indexOf(n)}`;
    }
    map.set(`${n.tagName}:${key}`, n);
  });
  return map;
}

/* 두 프레임에서 같은 키의 요소를 짝지어, 보간 가능한 속성마다 갱신 함수를 만든다 */
function pairMorph(a, b) {
  const ma = keyMap(a);
  const mb = keyMap(b);
  const updates = [];
  for (const [key, na] of ma) {
    const nb = mb.get(key);
    if (!nb) continue;
    for (const attr of Array.from(na.attributes)) {
      if (['id', 'data-key', 'clip-path', 'filter'].includes(attr.name)) continue;
      const to = nb.getAttribute(attr.name);
      if (to === null) continue;
      const fn = interpolate(attr.value, to);
      if (fn) {
        updates.push((t) => {
          const value = fn(t);
          na.setAttribute(attr.name, value);
          nb.setAttribute(attr.name, value);
        });
      }
    }
  }
  return updates;
}

/* 컨테이너 하나에 프레임을 그리는 플레이어. drawAt(초) 로 원하는 시점을 바로 그린다. */
export class MachinePlayer {
  constructor(container, states) {
    this.container = container;
    this.timeline = buildTimeline(states);
    this.templates = parseTemplates(states);
    this.currentSegment = -1;
    this.render = null;
  }

  prepare(seg) {
    const a = this.templates[seg.index].cloneNode(true);
    const b = seg.target === seg.index ? null : this.templates[seg.target].cloneNode(true);
    this.container.replaceChildren(a);
    if (b) this.container.appendChild(b);
    const updates = b && seg.type === 'SMART_ANIMATE' ? pairMorph(a, b) : [];
    this.render = (t) => {
      for (const apply of updates) apply(t);
      a.style.opacity = b ? String(1 - t) : '1';
      if (b) b.style.opacity = String(t);
    };
    this.currentSegment = seg.index;
  }

  drawAt(time) {
    const seg = locate(this.timeline, time);
    if (this.currentSegment !== seg.index) this.prepare(seg);
    this.render(seg.progress);
  }

  destroy() {
    this.container.replaceChildren();
    this.render = null;
    this.currentSegment = -1;
  }
}
